import { DOCUMENT, inject, Service } from '@angular/core'

/** sessionStorage key holding when this tab last reloaded itself into a new build. */
export const STALE_RELOAD_STORAGE_KEY = 'seiri:stale-reload-at'

/** A second reload inside this window means the reload did not help, so it is not attempted. */
export const STALE_RELOAD_WINDOW_MS = 10_000

const PREFETCH_TIMEOUT_MS = 3_000

/**
 * Moves a tab that is running an outdated build onto the current one, with a full page load to the
 * URL the user was navigating to. Guarded so that a failure the reload cannot fix - a broken deploy
 * rather than a stale tab - is reported instead of reloading in a loop. See docs/web-releases.md.
 */
@Service()
export class StaleBuildReloader {
  private readonly window = inject(DOCUMENT).defaultView
  private pending = false

  /** Whether this page is already on its way to the current build. */
  isReloading(): boolean {
    return this.pending
  }

  /**
   * Returns whether a reload is under way. `false` means the guard refused it and the caller still
   * owns the failure.
   */
  reloadTo(url: string): boolean {
    if (this.pending) {
      return true
    }
    const win = this.window
    if (!win || !this.claimReload(win)) {
      return false
    }
    this.pending = true

    const load = () => win.location.assign(url)
    // Refreshes the HTTP cache entry for the app shell first. nginx sends no Cache-Control for
    // index.html, so a plain page load may be answered from a heuristically cached copy that still
    // names the old chunks. This is the static shell, not an API endpoint, so it does not go through
    // the api layer. Whatever the outcome, the page load still happens.
    win.fetch(url, { cache: 'reload', signal: AbortSignal.timeout(PREFETCH_TIMEOUT_MS) }).then(load, load)
    return true
  }

  private claimReload(win: Window): boolean {
    try {
      const now = Date.now()
      const last = Number(win.sessionStorage.getItem(STALE_RELOAD_STORAGE_KEY))
      if (last && now - last < STALE_RELOAD_WINDOW_MS) {
        return false
      }
      win.sessionStorage.setItem(STALE_RELOAD_STORAGE_KEY, String(now))
      return true
    } catch {
      // Without storage there is no loop guard, and an unguarded reload on a failure that survives
      // it reloads forever. Leave the error to the caller instead.
      return false
    }
  }
}
